// AGC: PlotSummary - Plot creation confirmation screen
import React from 'react'
import { Check, MapPin, Ruler, Leaf, Tag } from 'lucide-react'
import { TopBar, GlassCard, Button } from '../shared'
import { formatArea } from '@droidfarm/shared'
import { CROP_TYPES, type Screen, type PlotFormData } from '../../types'
import toast from 'react-hot-toast'

interface PlotSummaryProps {
  plotData: PlotFormData
  onNavigate: (screen: Screen) => void
  onBack: () => void
}

/**
 * AGC: PlotSummary - Review and confirm plot creation
 */
export const PlotSummary: React.FC<PlotSummaryProps> = ({ plotData, onNavigate, onBack }) => {
  const cropInfo = CROP_TYPES.find(c => c.id === plotData.cropType)

  // AGC: Handle plot creation
  const handleCreate = () => {
    // AGC: In production, this would call an API
    toast.success('Talhão criado com sucesso!', {
      icon: '🌱',
      duration: 3000,
    })
    
    // AGC: Navigate back to fields list
    onNavigate('fields')
  }

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      {/* AGC: Top Bar */}
      <TopBar 
        title="Confirmar Talhão"
        onBack={onBack}
      />

      {/* AGC: Success Preview */}
      <div className="px-4 py-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-3">
            <Check className="w-8 h-8 text-[#0B5F4A]" />
          </div>
          <h2 className="text-gray-900">Tudo pronto!</h2>
          <p className="text-gray-500 mt-1">Revise as informações do novo talhão</p>
        </div>

        {/* AGC: Summary Card */}
        <GlassCard className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{plotData.name}</h3>
          
          <div className="space-y-4">
            {/* AGC: Crop Type */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <span className="text-xl">{cropInfo?.icon}</span>
              </div>
              <div>
                <p className="text-sm text-gray-500">Cultura</p>
                <p className="font-medium text-gray-900">{cropInfo?.name}</p>
              </div>
            </div>

            {/* AGC: Variety */}
            {plotData.variety && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <Tag className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Variedade</p>
                  <p className="font-medium text-gray-900">{plotData.variety}</p>
                </div>
              </div>
            )}

            {/* AGC: Area */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Ruler className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Área</p>
                <p className="font-medium text-gray-900">{formatArea(plotData.area)}</p>
              </div>
            </div>

            {/* AGC: Coordinates */}
            {plotData.coordinates && plotData.coordinates.length > 0 && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Limites</p>
                  <p className="font-medium text-gray-900">
                    {plotData.coordinates.length} pontos definidos
                  </p>
                </div>
              </div>
            )}
          </div>
        </GlassCard>

        {/* AGC: Next Steps Info */}
        <GlassCard className="bg-blue-50 border-blue-200">
          <div className="flex gap-3">
            <span className="text-xl">💡</span>
            <div>
              <p className="font-medium text-blue-800">Próximos passos</p>
              <ul className="text-sm text-blue-700 mt-1 space-y-1">
                <li>• Adicione a data de plantio</li>
                <li>• Configure a irrigação</li>
                <li>• Receba sugestões de manejo</li>
              </ul>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* AGC: Footer with buttons - safe-area-bottom for Android nav bar */}
      <div
        className="fixed bottom-0 left-0 right-0 px-4 pt-4 bg-white border-t border-gray-200 space-y-2"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
      >
        <Button fullWidth onClick={handleCreate}>
          Criar Talhão
        </Button>
        <Button 
          fullWidth 
          variant="secondary" 
          onClick={() => onNavigate('crop-suggestions')}
        >
          Ver Sugestões de Cultivo
        </Button>
      </div>
    </div>
  )
}

export default PlotSummary







